/**
 * Shop & Kits editor — admin tool for managing both individual items and
 * multi-item "kits" sold in the public shop / handed out by `/givekit`.
 *
 * The legacy version made admins type raw RCON commands by hand. This one:
 *   - Lets you SEARCH the full 1,969-item catalog with bot-canonical
 *     category buckets (Pal Saddles, Schematics, Eggs, etc.) and add
 *     entries with a quantity in one click; the right
 *     `give {steamid} <id> <qty>` line is generated automatically.
 *   - Lets you CLICK an existing kit to load it back into the form for
 *     a price/description tweak (no more retyping).
 *   - Lets you SEARCH the existing kits list, with a shop/hidden filter.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader, Field, Currency } from "@/components/Atoms";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { describe as describeCatalogItem } from "@/lib/shopBoilerplate";
import { Plus, Trash2, Search, ShoppingCart, EyeOff, CheckSquare, Square, Percent, Coins, Eye, Package } from "lucide-react";

// Parse an existing kit's raw command list back into our visual contents
// list so editing an existing kit doesn't lose the line-by-line structure.
const GIVE_RE = /^give\s+\{(?:steamid|userid)\}\s+(\S+)\s+(\d+)\s*$/i;

/**
 * Live render of what the public Shop card will look like — mirrors the
 * `<ShopCard>` and `<BuyModal>` "You'll receive" panel in `frontend/
 * src/pages/Shop.jsx` pixel-for-pixel so admins see exactly what the
 * player sees. Read-only; the Buy button is disabled.
 *
 * Two surfaces, both visible at once:
 *   1) catalog card — the tile players scroll past
 *   2) checkout preview — the "You'll receive" panel inside the Buy
 *      modal (only matters for multi-item bundles)
 */
function KitPreview({ name, description, price, contents, catalogById }) {
  const ASSETS = process.env.REACT_APP_BACKEND_URL;
  const itemRows = contents.filter((c) => c.type === "item");
  const firstItem = itemRows[0] ? catalogById.get(itemRows[0].id) : null;
  const isHidden = !(price > 0);
  const isMulti = itemRows.length > 1;

  return (
    <div className="rounded-lg border border-[var(--teal)]/30 bg-[rgba(0,229,255,0.04)] p-3 mb-4" data-testid="kit-preview">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--teal)] flex items-center gap-1.5 mb-2">
        <Eye size={11} /> Live preview · {isHidden ? "hidden (admin only)" : "public shop"}
      </div>

      {/* 1. Catalog card mirror — matches Shop.jsx::ShopCard exactly so
            admins can confirm icon/name/desc/price render the way a
            player will see them on the Shop page. */}
      <div
        className="glass-card p-4 flex flex-col"
        data-testid="kit-preview-card"
      >
        <div className="flex items-start gap-3 mb-2">
          {firstItem?.image ? (
            <img
              src={`${ASSETS}${firstItem.image}`}
              alt={name}
              className="w-12 h-12 rounded bg-black/40 object-contain p-1 shrink-0"
              draggable={false}
            />
          ) : (
            <div className="w-12 h-12 rounded bg-black/40 flex items-center justify-center shrink-0">
              <Package size={24} className="text-[var(--teal)]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="kit-preview-name">
              {name || <span className="text-[var(--muted)] italic">Untitled item</span>}
            </div>
            {description ? (
              <div className="text-[11px] text-[var(--muted)] line-clamp-2" data-testid="kit-preview-desc">{description}</div>
            ) : (
              <div className="text-[11px] text-[var(--muted)] italic">No description set</div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-auto pt-2">
          {isHidden ? (
            <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]" data-testid="kit-preview-hidden">
              Hidden · admin-only
            </span>
          ) : (
            <Currency value={parseInt(price, 10) || 0} data-testid="kit-preview-price" />
          )}
          <button
            type="button"
            disabled
            className="btn btn-sm opacity-60 cursor-not-allowed"
            title="Preview only"
            data-testid="kit-preview-buy"
          >
            Buy
          </button>
        </div>
      </div>

      {/* 2. Checkout / Buy-modal mirror — only renders for multi-item
            bundles; this is the "You'll receive" panel that pops up
            after the player clicks Buy. Lets admins double-check the
            line-items + quantities before publishing. Single-item
            listings show their full contents on the card above, so
            duplicating it here would just be noise. */}
      {isMulti && (
        <div
          className="mt-2 rounded bg-black/30 p-3 border border-white/5"
          data-testid="kit-preview-receive"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5 flex items-center justify-between">
            <span>You&rsquo;ll receive</span>
            <span className="font-mono text-[10px]">{itemRows.length} entries</span>
          </div>
          <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
            {itemRows.map((c, idx) => {
              const it = catalogById.get(c.id);
              return (
                <li key={idx} className="flex items-center gap-2 text-[var(--bone)]" data-testid={`kit-preview-row-${idx}`}>
                  {it?.image ? (
                    <img src={`${ASSETS}${it.image}`} alt="" className="w-4 h-4 object-contain shrink-0" />
                  ) : (
                    <span className="w-4 h-4 shrink-0 inline-block" />
                  )}
                  <span className="truncate">{c.qty}× {it?.name || c.id}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function normalizeCommands(cmds) {
  if (Array.isArray(cmds)) return cmds;
  const s = String(cmds || "").trim();
  if (s.startsWith("[")) {
    try {
      const arr = JSON.parse(s);
      if (Array.isArray(arr)) return arr.map(String);
    } catch { /* fall through to newline split */ }
  }
  return s ? s.split("\n") : [];
}

function parseCommandsToContents(cmds) {
  if (!cmds) return [];
  const list = normalizeCommands(cmds);
  return list.map((line) => {
    const m = (line || "").trim().match(GIVE_RE);
    if (!m) return { type: "raw", raw: line };
    return { type: "item", id: m[1], qty: parseInt(m[2], 10) || 1 };
  }).filter((c) => c.type !== "raw" || c.raw);
}

function contentsToCommands(contents, lookup) {
  return contents.map((c) => {
    if (c.type === "raw") return c.raw;
    const it = lookup.get(c.id);
    return `give {steamid} ${c.id} ${c.qty}`;
  }).filter(Boolean);
}

export function Kits() {
  const toast = useToast();
  const [kits, setKits] = useState([]);
  const [loadingKits, setLoadingKits] = useState(true);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("all"); // all | shop | hidden
  // Which DB table we're managing: "items" = `kits` (individual shop items),
  // "bundles" = `admin_kits` (the public shop's Kits tab + /givekit bundles).
  const [table, setTable] = useState("items");
  const kitsEndpoint = table === "bundles" ? "/admin/bundle-kits" : "/admin/kits";

  // Form state — composing a single kit/item entry.
  const [editing, setEditing] = useState(null); // original name we're editing, or null = new
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState(0);
  const [contents, setContents] = useState([]); // [{type:'item', id, qty} | {type:'raw', raw}]
  const [rawMode, setRawMode] = useState(false); // power-user toggle for direct command editing
  const [rawText, setRawText] = useState("");

  // Item picker state.
  const [catalog, setCatalog] = useState([]);
  const [pickerKind, setPickerKind] = useState("item"); // item | pal | egg | tech
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerCategory, setPickerCategory] = useState("");
  const [pickerQty, setPickerQty] = useState(1);

  // Quick give.
  const [give, setGive] = useState({ player_name: "", kit_name: "", server_name: "" });

  // Smart Quick-Give helpers: configured servers + currently-online players
  // so the admin clicks instead of typing names (and can't typo a server).
  const [servers, setServers] = useState([]);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [giveBusy, setGiveBusy] = useState(false);

  // Kit list sort. Defaults to alphabetical so newly-added kits are easy
  // to find; "price-low"/"price-high" surface stock for sale/discount work.
  const [sortMode, setSortMode] = useState("name"); // name | price-low | price-high

  // Power-user toggle in the editor — peek the literal RCON commands
  // that will fire when the kit is bought / granted. Off by default
  // (the live preview is the better lens for 99 % of edits).
  const [showCommands, setShowCommands] = useState(false);

  // Bulk price update — keeps prices safe by routing through a dedicated
  // backend endpoint that ONLY touches `price`, never name/commands/desc.
  // The per-kit edit form remains the canonical way to change anything else.
  const [bulkSelection, setBulkSelection] = useState(new Set());
  const [bulkMode, setBulkMode] = useState("percent");   // percent | delta
  const [bulkValue, setBulkValue] = useState(10);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleBulk = (name) => {
    setBulkSelection((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const selectAllVisible = (visible) => {
    setBulkSelection((prev) => {
      const next = new Set(prev);
      visible.forEach((k) => next.add(k.name));
      return next;
    });
  };
  const clearBulk = () => setBulkSelection(new Set());

  const applyBulk = async () => {
    if (bulkSelection.size === 0) return;
    const v = parseFloat(bulkValue);
    if (Number.isNaN(v)) return toast.error("Enter a valid number");
    const sign = v > 0 ? "+" : "";
    const human = bulkMode === "percent" ? `${sign}${v}%` : `${sign}${v} coins`;
    if (!window.confirm(`Apply ${human} to ${bulkSelection.size} kit${bulkSelection.size > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/kits/bulk-price", {
        names: [...bulkSelection],
        mode: bulkMode,
        value: v,
      });
      toast.success(`Updated ${data.updated} (${data.skipped} unchanged)`);
      clearBulk();
      loadKits();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const loadKits = () =>
    api.get(kitsEndpoint).then(({ data }) => setKits(data.kits || []))
      .finally(() => setLoadingKits(false));

  const loadCatalog = (kind) =>
    api.get(`/admin/give/catalog?kind=${kind}`).then(({ data }) => setCatalog(data.results || []));

  useEffect(() => {
    setLoadingKits(true);
    setKits([]);
    resetForm();
    clearBulk();
    loadKits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);
  useEffect(() => { loadCatalog(pickerKind); setPickerCategory(""); setPickerSearch(""); }, [pickerKind]);

  // Load configured servers + currently-online players once so the Quick
  // Give form can offer real dropdowns. Both endpoints already exist
  // and are 4 s-cached on the backend, so re-running this on the
  // (rare) tab switch is cheap and not worth a polling timer.
  useEffect(() => {
    api.get("/live/servers")
      .then(({ data }) => setServers(data.servers || []))
      .catch(() => setServers([]));
    api.get("/link/online-players")
      .then(({ data }) => setOnlinePlayers(Array.isArray(data) ? data : (data.players || [])))
      .catch(() => setOnlinePlayers([]));
  }, []);

  // Catalog → quick lookup by id (used to render names/icons inline in the
  // kit contents list without a second roundtrip).
  const catalogById = useMemo(() => {
    const m = new Map();
    catalog.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [catalog]);

  // Category dropdown options derived from the loaded catalog.
  const catalogCategories = useMemo(() => {
    const counts = new Map();
    catalog.forEach((c) => counts.set(c.category || "Other", (counts.get(c.category || "Other") || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [catalog]);

  const filteredPicker = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return catalog.filter((c) => {
      if (pickerCategory && (c.category || "Other") !== pickerCategory) return false;
      if (!q) return true;
      return c.name?.toLowerCase().includes(q) || c.id?.toLowerCase().includes(q);
    }).slice(0, 100);
  }, [catalog, pickerSearch, pickerCategory]);

  // Kits list with search + status filter + sort.
  const filteredKits = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matched = kits.filter((k) => {
      if (tab === "shop" && !(k.price > 0)) return false;
      if (tab === "hidden" && k.price > 0) return false;
      if (!q) return true;
      return (k.name || "").toLowerCase().includes(q) || (k.description || "").toLowerCase().includes(q);
    });
    const sorted = [...matched];
    if (sortMode === "price-low") {
      sorted.sort((a, b) => (a.price || 0) - (b.price || 0) || (a.name || "").localeCompare(b.name || ""));
    } else if (sortMode === "price-high") {
      sorted.sort((a, b) => (b.price || 0) - (a.price || 0) || (a.name || "").localeCompare(b.name || ""));
    } else {
      sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return sorted;
  }, [kits, filter, tab, sortMode]);

  const resetForm = () => {
    setEditing(null);
    setName(""); setDesc(""); setPrice(0);
    setContents([]); setRawMode(false); setRawText("");
  };

  const loadIntoForm = (kit) => {
    setEditing(kit.name);
    setName(kit.name);
    setDesc(kit.description || "");
    setPrice(kit.price || 0);
    const parsed = parseCommandsToContents(kit.commands);
    setContents(parsed);
    // If any line couldn't be parsed back into an item, flip on raw mode
    // so the admin can still see/edit it without data loss.
    const hasUnparsed = parsed.some((c) => c.type === "raw");
    setRawMode(hasUnparsed);
    setRawText(normalizeCommands(kit.commands).join("\n"));
  };

  const addToContents = (catalogItem) => {
    // Auto-fill the kit Name from the first picked item when the admin
    // hasn't typed one yet (and we're creating, not editing). Saves the
    // common case of "single-item kit" where Name was always a copy of
    // the catalog item's display name.
    if (!editing && !name.trim() && catalogItem?.name) {
      setName(catalogItem.name);
    }
    // Similarly, auto-suggest a one-sentence Description from the
    // catalog item's category so single-item shop entries don't look
    // bare. Skipped if the admin already typed something.
    if (!editing && !desc.trim()) {
      const suggested = describeCatalogItem(catalogItem, pickerKind);
      if (suggested) setDesc(suggested);
    }
    setContents((prev) => {
      const existing = prev.findIndex((c) => c.type === "item" && c.id === catalogItem.id);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], qty: next[existing].qty + pickerQty };
        return next;
      }
      return [...prev, { type: "item", id: catalogItem.id, qty: pickerQty }];
    });
  };

  const updateQty = (idx, qty) => {
    setContents((prev) => prev.map((c, i) => (i === idx ? { ...c, qty: Math.max(1, qty) } : c)));
  };

  const removeFromContents = (idx) => {
    setContents((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("Name is required");
    const commands = rawMode
      ? rawText.split("\n").map((s) => s.trim()).filter(Boolean)
      : contentsToCommands(contents, catalogById);
    if (commands.length === 0) return toast.error("Add at least one item or RCON command");
    try {
      await api.post(kitsEndpoint, {
        name: name.trim(),
        description: desc.trim(),
        commands,
        price: parseInt(price, 10) || 0,
      });
      toast.success(editing ? `Updated ${editing}` : `Created ${name}`);
      resetForm();
      loadKits();
    } catch (err) { toast.error(apiError(err)); }
  };

  const del = async (n) => {
    if (!window.confirm(`Delete ${n}?`)) return;
    try {
      await api.delete(`${kitsEndpoint}/${encodeURIComponent(n)}`);
      toast.success(`Deleted ${n}`);
      if (editing === n) resetForm();
      loadKits();
    } catch (err) { toast.error(apiError(err)); }
  };

  // Clone an existing kit into the form as a NEW entry so the admin can
  // tweak it (price/contents/description) and save as a variant without
  // overwriting the original. Suffixes " (Copy)" — admin will rename it.
  const duplicate = (kit) => {
    setEditing(null); // critical — make sure save creates instead of updates
    const base = (kit.name || "").replace(/\s*\(Copy(?:\s\d+)?\)\s*$/i, "");
    let suffix = " (Copy)";
    let n = 2;
    const existing = new Set(kits.map((k) => k.name));
    while (existing.has(base + suffix)) {
      suffix = ` (Copy ${n++})`;
    }
    setName(base + suffix);
    setDesc(kit.description || "");
    setPrice(kit.price || 0);
    const parsed = parseCommandsToContents(kit.commands);
    setContents(parsed);
    const hasUnparsed = parsed.some((c) => c.type === "raw");
    setRawMode(hasUnparsed);
    setRawText(normalizeCommands(kit.commands).join("\n"));
    toast.success(`Cloned ${kit.name} — edit & save`);
    // Scroll to the top of the editor on small screens so the admin sees the
    // form populated (the kits list is right of the form on lg+, below on mobile).
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Pre-fill the Quick Give form with this kit's name so the admin only
  // has to pick player + server. Saves the most common typo.
  const prefillGive = (kitName) => {
    setGive((g) => ({ ...g, kit_name: kitName }));
    toast.success(`Quick Give pre-filled with ${kitName}`);
    const el = document.querySelector('[data-testid="give-kit-form"]');
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const giveKit = async (e) => {
    e.preventDefault();
    if (!give.player_name || !give.kit_name || !give.server_name) {
      toast.error("Pick player, kit, and server");
      return;
    }
    setGiveBusy(true);
    try {
      const { data } = await api.post("/admin/kits/give", give);
      toast.success(`Queued ${give.kit_name} for ${data.player || give.player_name}`);
      // Keep server + kit selected (admins often hand the same kit to
      // multiple players in a row) — only clear the player field.
      setGive((g) => ({ ...g, player_name: "" }));
    } catch (err) { toast.error(apiError(err)); }
    finally { setGiveBusy(false); }
  };

  return (
    <div className="space-y-4 fade-up">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Shop &amp; Kits Editor</h3>
        <p className="text-xs text-[var(--muted)]">
          Click an entry to <strong>edit</strong> it. <Plus size={11} className="inline -mt-0.5"/> on a row clones a copy you can rename, and <Package size={11} className="inline -mt-0.5"/> pre-fills the Quick-Give form below.
          Add items via the catalog — they&rsquo;re translated to <code>give {"{steamid}"} ItemId Qty</code> automatically.
          Set <strong>Price &gt; 0</strong> to publish to the public Shop, or
          <strong> Price = 0</strong> to keep it admin-only.
        </p>
        <div className="flex gap-2 mt-3">
          {[
            ["items", ShoppingCart, "Shop Items"],
            ["bundles", Package, "Kits (Bundles)"],
          ].map(([id, Icon, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTable(id)}
              className={`btn btn-sm ${table === id ? "" : "btn-secondary"}`}
              data-testid={`kits-table-${id}`}
            >
              <Icon size={13} className="inline -mt-0.5 mr-1" />{label}
            </button>
          ))}
        </div>
        {table === "bundles" && (
          <p className="text-[11px] text-[var(--muted)] mt-2" data-testid="bundles-hint">
            Kit bundles are what the public Shop&rsquo;s <strong>Kits</strong> tab sells and what
            <code className="mx-1">/givekit</code> delivers on Discord (the <code>admin_kits</code> table).
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* ===================== Editor form ===================== */}
        <form onSubmit={save} className="glass-card p-5 space-y-3" data-testid="kit-form">
          <div className="flex items-center justify-between">
            <h3 className="label">
              {editing ? <>Editing <span className="text-[var(--bone)]">{editing}</span></> : "New Item / Kit"}
            </h3>
            {editing && (
              <button type="button" onClick={resetForm} className="text-xs text-[var(--muted)] hover:text-[var(--bone)]" data-testid="kit-cancel-edit">
                + Create new instead
              </button>
            )}
          </div>

          {/* Live preview — mirrors how this listing will render in the
              public Shop right now. Updates as the admin types or picks
              items. Hidden in raw-command mode (no contents to render). */}
          {!rawMode && (
            <KitPreview
              name={name}
              description={desc}
              price={price}
              contents={contents}
              catalogById={catalogById}
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name">
              <input className="input" required value={name} onChange={(e) => setName(e.target.value)} data-testid="kit-name-input" />
            </Field>
            <Field label="Price (0 = hidden)">
              <input className="input" type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="kit-price-input" />
            </Field>
          </div>
          <Field label="Description">
            <input className="input" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="kit-desc-input" />
          </Field>

          {/* Contents builder */}
          <div className="border-t border-white/5 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="label">Contents ({contents.length})</div>
              <label className="text-xs flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="accent-[var(--teal)]"
                  checked={rawMode}
                  onChange={(e) => setRawMode(e.target.checked)}
                  data-testid="kit-raw-toggle"
                />
                Raw command mode
              </label>
            </div>

            {rawMode ? (
              <textarea
                className="input font-mono text-xs"
                rows={6}
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="give {steamid} PalSphere 10"
                data-testid="kit-raw-textarea"
              />
            ) : (
              <>
                {contents.length === 0 ? (
                  <p className="text-xs text-[var(--muted)] italic py-2">Pick items from the catalog below ↓</p>
                ) : (
                  <ul className="divide-y divide-white/5 max-h-48 overflow-y-auto border border-white/5 rounded bg-black/20">
                    {contents.map((c, i) => {
                      const it = c.type === "item" ? catalogById.get(c.id) : null;
                      return (
                        <li key={i} className="flex items-center gap-2 px-2 py-1.5 text-sm" data-testid={`kit-content-${i}`}>
                          {it?.image ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${it.image}`} alt="" className="w-7 h-7 object-contain shrink-0" />
                          ) : <div className="w-7 h-7 bg-black/40 rounded shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{it?.name || (c.type === "raw" ? c.raw : c.id)}</div>
                            {it && <div className="text-[10px] text-[var(--muted)] truncate">{c.id} · {it.category}</div>}
                          </div>
                          {c.type === "item" && (
                            <input
                              type="number"
                              min="1"
                              value={c.qty}
                              onChange={(e) => updateQty(i, parseInt(e.target.value, 10) || 1)}
                              className="input w-16 text-right"
                              data-testid={`kit-qty-${i}`}
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removeFromContents(i)}
                            className="text-[var(--warning)] hover:text-[var(--warning-strong,#ff6b35)] p-1"
                            data-testid={`kit-remove-${i}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn flex-1" data-testid="kit-save-button">
              {editing ? "Update" : "Create"}
            </button>
            {editing && (
              <>
                <button
                  type="button"
                  onClick={() => duplicate({ name, description: desc, price, commands: contentsToCommands(contents, catalogById) })}
                  className="btn btn-secondary"
                  data-testid="kit-duplicate-button"
                  title="Save as a new copy"
                >
                  Duplicate
                </button>
                <button type="button" onClick={() => del(editing)} className="btn btn-danger" data-testid="kit-delete-button">
                  Delete
                </button>
              </>
            )}
          </div>

          {/* Power-user peek at the literal RCON commands that will run.
              Off by default — the live shop preview above is the right
              lens for almost every edit. */}
          {!rawMode && contents.length > 0 && (
            <div className="border-t border-white/5 pt-2 mt-1">
              <button
                type="button"
                onClick={() => setShowCommands((v) => !v)}
                className="text-[11px] text-[var(--muted)] hover:text-[var(--bone)] flex items-center gap-1"
                data-testid="kit-toggle-commands"
              >
                {showCommands ? "▾" : "▸"} View RCON commands ({contentsToCommands(contents, catalogById).length})
              </button>
              {showCommands && (
                <pre
                  className="mt-2 text-[11px] font-mono bg-black/30 border border-white/5 rounded p-2 overflow-x-auto leading-relaxed"
                  data-testid="kit-commands-preview"
                >
                  {contentsToCommands(contents, catalogById).join("\n")}
                </pre>
              )}
            </div>
          )}
        </form>

        {/* ===================== Existing kits ===================== */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="label">{table === "bundles" ? "Kit Bundles" : "All Items & Kits"} ({kits.length})</h3>
            <div className="flex gap-1 flex-wrap">
              {[
                ["all",    `All (${kits.length})`],
                ["shop",   `Shop (${kits.filter((k) => k.price > 0).length})`],
                ["hidden", `Hidden (${kits.filter((k) => !k.price).length})`],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={`btn btn-sm ${tab === id ? "" : "btn-secondary"}`}
                  data-testid={`kit-filter-${id}`}
                >{label}</button>
              ))}
              <select
                className="input text-xs h-8 py-0 px-2"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                data-testid="kit-sort"
                title="Sort kits"
              >
                <option value="name">A → Z</option>
                <option value="price-low">Price ↑</option>
                <option value="price-high">Price ↓</option>
              </select>
            </div>
          </div>
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              className="input pl-9"
              placeholder="Search kits…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="kit-search-input"
            />
          </div>
          {loadingKits ? <Loader /> : (
            <>
              {/* Bulk-price action bar — shown only when at least one row
                  is selected. Routes through `/admin/kits/bulk-price`
                  which is price-only by design so single-kit edits made
                  in the form (or by the bot) can't be clobbered. */}
              {bulkSelection.size > 0 && (
                <div
                  className="rounded border border-[var(--teal)]/40 bg-[var(--teal-soft)] p-3 mb-3 text-sm"
                  data-testid="bulk-price-bar"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">
                      {bulkSelection.size} selected
                    </div>
                    <button
                      type="button"
                      onClick={clearBulk}
                      className="text-xs text-[var(--muted)] hover:text-[var(--bone)]"
                      data-testid="bulk-clear"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_auto] gap-2 items-center">
                    <select
                      className="input"
                      value={bulkMode}
                      onChange={(e) => setBulkMode(e.target.value)}
                      data-testid="bulk-mode"
                    >
                      <option value="percent">Percent</option>
                      <option value="delta">Flat</option>
                    </select>
                    <div className="relative">
                      {bulkMode === "percent"
                        ? <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                        : <Coins size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />}
                      <input
                        type="number"
                        className="input pr-9"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        placeholder={bulkMode === "percent" ? "+10 = +10%" : "+100 = +100 coins"}
                        data-testid="bulk-value"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={applyBulk}
                      disabled={bulkBusy}
                      className="btn"
                      data-testid="bulk-apply"
                    >
                      {bulkBusy ? "Applying…" : "Apply"}
                    </button>
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-2">
                    Price-only. Doesn&rsquo;t touch contents, name, or descriptions.
                    Negative values are allowed for discounts. Prices clamp at 0.
                  </div>
                </div>
              )}

              {/* "Select all visible" tap — bulk pricing targets the `kits`
                  table only, so it's hidden in Bundles mode. */}
              {table === "items" && filteredKits.length > 0 && (
                <div className="flex items-center justify-between text-[11px] text-[var(--muted)] mb-2 px-1">
                  <button
                    type="button"
                    onClick={() => selectAllVisible(filteredKits)}
                    className="hover:text-[var(--teal)] inline-flex items-center gap-1"
                    data-testid="bulk-select-all"
                  >
                    <CheckSquare size={12} /> Select all {filteredKits.length} visible
                  </button>
                </div>
              )}

              <ul className="divide-y divide-white/5 max-h-[28rem] overflow-y-auto">
                {filteredKits.map((k) => {
                  const checked = bulkSelection.has(k.name);
                  return (
                    <li
                      key={k.name}
                      className="py-2 text-sm flex items-start gap-2"
                      data-testid={`kit-row-${k.name.replace(/\s/g, "-")}`}
                    >
                      {table === "items" && (
                      <button
                        type="button"
                        onClick={() => toggleBulk(k.name)}
                        className={`mt-1 shrink-0 ${checked ? "text-[var(--teal)]" : "text-[var(--muted)] hover:text-[var(--bone)]"}`}
                        aria-label={checked ? "Deselect" : "Select"}
                        data-testid={`bulk-toggle-${k.name.replace(/\s/g, "-")}`}
                      >
                        {checked ? <CheckSquare size={16} /> : <Square size={16} />}
                      </button>
                      )}
                      <button
                        type="button"
                        onClick={() => loadIntoForm(k)}
                        className={`flex-1 text-left hover:bg-white/5 rounded px-2 py-1 transition-colors ${
                          editing === k.name ? "bg-[var(--teal-soft)] border border-[var(--teal)]/40" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {k.price > 0
                              ? <ShoppingCart size={12} className="text-[var(--teal)] shrink-0" />
                              : <EyeOff size={12} className="text-[var(--muted)] shrink-0" />}
                            <span className="truncate">{k.name}</span>
                          </div>
                          {k.price > 0 ? (
                            <span className="text-[var(--muted)] inline-flex items-center gap-1 shrink-0">
                              {k.price.toLocaleString()}<ChilletCoinIcon size={11}/>
                            </span>
                          ) : (
                            <span className="text-[10px] text-[var(--muted)] shrink-0">hidden</span>
                          )}
                        </div>
                        {k.description && <div className="text-[11px] text-[var(--muted)] mt-0.5 truncate">{k.description}</div>}
                      </button>
                      <div className="flex gap-1 mt-0.5 shrink-0 opacity-50 hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => prefillGive(k.name)}
                          className="p-1 text-[var(--muted)] hover:text-[var(--teal)]"
                          title={`Quick-give ${k.name} to a player`}
                          data-testid={`kit-give-${k.name.replace(/\s/g, "-")}`}
                        >
                          <Package size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => duplicate(k)}
                          className="p-1 text-[var(--muted)] hover:text-[var(--teal)]"
                          title={`Duplicate ${k.name}`}
                          data-testid={`kit-duplicate-row-${k.name.replace(/\s/g, "-")}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </li>
                  );
                })}
                {filteredKits.length === 0 && (
                  <li className="py-6 text-center text-xs text-[var(--muted)]">No kits match.</li>
                )}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* ===================== Item picker (full-width) ===================== */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="label">Add from catalog</h3>
          <div className="flex gap-1">
            {["item", "pal", "egg", "tech"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setPickerKind(k)}
                className={`btn btn-sm ${pickerKind === k ? "" : "btn-secondary"}`}
                data-testid={`picker-kind-${k}`}
              >{k}s</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_220px_110px] gap-2 mb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              className="input pl-9"
              placeholder={`Search ${catalog.length} ${pickerKind}s…`}
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              data-testid="picker-search"
            />
          </div>
          <select
            className="input"
            value={pickerCategory}
            onChange={(e) => setPickerCategory(e.target.value)}
            data-testid="picker-category"
          >
            <option value="">All categories ({catalog.length})</option>
            {catalogCategories.map(([n, count]) => (
              <option key={n} value={n}>{n} ({count})</option>
            ))}
          </select>
          <Field label="Qty">
            <input
              className="input"
              type="number"
              min="1"
              value={pickerQty}
              onChange={(e) => setPickerQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
              data-testid="picker-qty"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[24rem] overflow-y-auto">
          {filteredPicker.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => addToContents(c)}
              disabled={rawMode}
              title={`${c.id} · ${c.category}`}
              className="text-left bg-black/30 hover:bg-[var(--teal-soft)] border border-white/5 hover:border-[var(--teal)]/40 rounded p-2 transition-colors disabled:opacity-40"
              data-testid={`picker-item-${c.id}`}
            >
              <div className="flex items-center gap-2">
                {c.image ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${c.image}`} alt="" className="w-9 h-9 object-contain shrink-0" />
                ) : <div className="w-9 h-9 bg-black/40 rounded shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate">{c.name}</div>
                  <div className="text-[10px] text-[var(--muted)] truncate">{c.category}</div>
                </div>
                <Plus size={12} className="text-[var(--teal)] shrink-0" />
              </div>
            </button>
          ))}
          {filteredPicker.length === 0 && (
            <p className="col-span-full text-center text-xs text-[var(--muted)] py-6">No matches.</p>
          )}
        </div>
      </div>

      {/* ===================== Quick give ===================== */}
      <form onSubmit={giveKit} className="glass-card p-5 space-y-3" data-testid="give-kit-form">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="label">Give Kit to Player</h3>
          <p className="text-[11px] text-[var(--muted)]">
            {onlinePlayers.length} online · {servers.length} server{servers.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label={`Server${give.server_name ? "" : " (pick one)"}`}>
            <select
              className="input"
              required
              value={give.server_name}
              onChange={(e) => setGive({ ...give, server_name: e.target.value, player_name: "" })}
              data-testid="give-server-select"
            >
              <option value="">— select —</option>
              {servers.map((s) => (
                <option key={s.name || s.slug} value={s.name}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label={`Player${onlinePlayers.length ? " (online players autocomplete)" : ""}`}>
            <input
              className="input"
              required
              list="give-online-players"
              placeholder={onlinePlayers.length ? "Start typing…" : "Type the in-game name"}
              value={give.player_name}
              onChange={(e) => setGive({ ...give, player_name: e.target.value })}
              data-testid="give-player-input"
            />
            <datalist id="give-online-players">
              {onlinePlayers
                .filter((p) => !give.server_name || p.server_name === give.server_name)
                .map((p) => (
                  <option key={`${p.server_name}-${p.player_name}`} value={p.player_name}>
                    {p.server_name}{p.level ? ` · L${p.level}` : ""}
                  </option>
                ))}
            </datalist>
          </Field>
          <Field label={`Kit${give.kit_name ? "" : " (pick one)"}`}>
            <select
              className="input"
              required
              value={give.kit_name}
              onChange={(e) => setGive({ ...give, kit_name: e.target.value })}
              data-testid="give-kit-select"
            >
              <option value="">— select —</option>
              {[...kits].sort((a, b) => a.name.localeCompare(b.name)).map((k) => (
                <option key={k.name} value={k.name}>
                  {k.name}{k.price > 0 ? ` (${k.price.toLocaleString()} ¢)` : ""}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn" disabled={giveBusy} data-testid="give-kit-button">
            {giveBusy ? "Queueing…" : "Queue Delivery"}
          </button>
          {(give.kit_name || give.server_name || give.player_name) && (
            <button
              type="button"
              onClick={() => setGive({ player_name: "", kit_name: "", server_name: "" })}
              className="text-xs text-[var(--muted)] hover:text-[var(--bone)]"
              data-testid="give-clear"
            >
              Clear
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
